import type { PhysicalCardPrintRow } from '../api.js';

/**
 * The print file of «چاپ کارت» (AC §33.9): an `.xlsx` that opens right to left, with every cell as
 * text (a 16-digit number would lose digits in Excel and `037` its zero), or a CSV with a BOM. Both
 * are built in the browser from the P4 response and never stored. The sheet writer loads on first use.
 */

/** The file columns in order (tests and the print shop read the header). */
export const PHYSICAL_CARD_PRINT_COLUMNS: readonly (readonly [
  keyof PhysicalCardPrintRow,
  string,
])[] = [
  ['buyerId', 'شناسه خریدار'],
  ['holderNameFa', 'نام روی کارت'],
  ['cardNumber', 'شماره کارت'],
  ['accountNumber', 'شماره حساب'],
  ['cardSequenceNumber', 'شماره نسخه'],
  ['expiresOn', 'اعتبار تا'],
  ['expiryText', 'انقضا'],
  ['qrPayload', 'متن QR'],
  ['cvv2', 'CVV2'],
  ['seriesPrefix', 'سری کارت'],
  ['cityTag', 'کد شهر'],
  ['cityNameFa', 'شهر'],
  ['cardStatus', 'وضعیت'],
  ['issuedAt', 'زمان صدور'],
  // D-119: appended, so a print shop that reads the columns by position keeps working (Q-D119-07).
  ['cardNumberGrouped', 'شماره کارت چاپی'],
  ['expiryFull', 'انقضای کامل'],
  ['citySerial', 'سریال شهر'],
  ['qrText', 'لینک QR'],
];

const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function csvCell(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

export function printCsv(items: readonly PhysicalCardPrintRow[]): string {
  const header = PHYSICAL_CARD_PRINT_COLUMNS.map(([, label]) => csvCell(label)).join(',');
  const lines = items.map((row) =>
    PHYSICAL_CARD_PRINT_COLUMNS.map(([key]) =>
      csvCell(row[key] === null ? '' : String(row[key])),
    ).join(','),
  );
  return `﻿${[header, ...lines].join('\r\n')}`;
}

/** One text cell; `null` is an empty cell. `@` keeps it text when someone edits it in Excel. */
function textCell(value: string | number | null, bold = false) {
  if (value === null) return null;
  return {
    format: '@',
    type: String,
    value: String(value),
    ...(bold ? { fontWeight: 'bold' as const } : {}),
  };
}

export async function printXlsx(items: readonly PhysicalCardPrintRow[]): Promise<Blob> {
  const { default: writeExcelFile } = await import('write-excel-file/browser');
  const sheet = [
    PHYSICAL_CARD_PRINT_COLUMNS.map(([, label]) => textCell(label, true)),
    ...items.map((row) => PHYSICAL_CARD_PRINT_COLUMNS.map(([key]) => textCell(row[key]))),
  ];
  const blob = await writeExcelFile(sheet, {
    columns: PHYSICAL_CARD_PRINT_COLUMNS.map(([key]) => ({ width: key === 'qrText' ? 60 : 22 })),
    rightToLeft: true,
    stickyRowsCount: 1,
  }).toBlob();
  return new Blob([blob], { type: XLSX_TYPE });
}

function saveBlob(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function savePrintCsv(batchNumber: number, items: readonly PhysicalCardPrintRow[]): void {
  saveBlob(
    `saay-print-${batchNumber}.csv`,
    new Blob([printCsv(items)], { type: 'text/csv;charset=utf-8' }),
  );
}

export async function savePrintXlsx(
  batchNumber: number,
  items: readonly PhysicalCardPrintRow[],
): Promise<void> {
  saveBlob(`saay-print-${batchNumber}.xlsx`, await printXlsx(items));
}
